<template>
  <!-- List of sources -->
  <div v-if="data" :class="{'event-disabler': transporterPaneOpen}">
    <h4 class="card-list-title">
      {{$t('INDENT_SOURCE')}}
      <!-- used for multiple sources expand view -->
      <span
        v-if="(maxSelectionAllowed > data.length) && data.length > 1"
        class="expand-all f-right"
        @click="expandView = !expandView"
      >
        <span v-if="!expandView">Expand</span>
        <span v-else>Collapse</span>
      </span>
    </h4>
    <div
      class="card-list"
      :class="{'active': currentActiveIndex === index1}"
      v-for="(source,index1) in data"
      :key="index1"
      @click="changeCurrentActiveSource(index1)">
        <h5 class="title-with-icon clearfix">
          <i class="icon-source bg-green-light">
            <svgicon :name="getSourceIconByType(source.type)" width="13" height="13" />
          </i>
          <div>
            {{source.name}}
            <small>{{source.reference_id}}</small>
          </div>
          <Tooltip :open-delay="100" effect="dark" placement="top" v-if="!hideAllActions && showDelete">
            <div slot="content">{{$t('INDENT_DELETE_SOURCE')}}</div>
            <i class="action-icon" @click="showSourceDeleteDialog = true" :style="isIconVisible ? 'opacity:1;visibility:visible' : ''">
              <svgicon name="icon-delete-md" width="14" height="14"/>
            </i>
          </Tooltip>
        </h5>
        <span v-if="(expandView || currentActiveIndex === index1) &&
          showPickupsByType(source.type)"
        >
          <!-- <h6>Pickups</h6> -->
          <h5
            v-for="(pickup,index2) in source.pickups"
            :key="index2"
            :class="{'is-active':currentActivePickupIndex === index2}"
          >
              <span v-if="pickup.name">
                {{pickup.name}}
              </span>
              <span class="text-grey" v-else>{{$t('INDENT_SELECT_GATE')}}</span>
              <small>{{pickup.reference_id}}</small>
              <!-- <Tooltip :open-delay="100" effect="dark" placement="top" v-if="!hideAllActions">
                <div slot="content">Delete Gate</div>
                <i class="action-icon">
                  <svgicon name="delete" width="10" height="10"
                    @click="deletePickup(index2, index1)"
                  />
                </i>
              </Tooltip> -->
          </h5>
        </span>
    </div>
    <div class="card-list" v-if="selectionInProgress">
      <h5>
        <i class="icon-source bg-green-light">
          <svgicon name="icon-source" width="13" height="13" />
        </i>
        <span class="text-green">{{$t('INDENT_SELECT_SOURCE')}}</span>
      </h5>
    </div>
    <div
      v-else-if="(maxSelectionAllowed > data.length) && !hideAllActions && canAddPickup"
      class="card-list list-plain" @click="$emit('addNewSource')"
    >
      <h5>
        <span v-if="noDataFound">{{$t('INDENT_ADD_SOURCE_LOCATION')}}</span>
        <span v-else>{{$t('INDENT_ADDITIONAL_SOURCE')}}</span>
        <small>{{$t('INDENT_CLICK_TO_ADD')}}
          <span
            v-for="(config,index) in sourceConfig"
            v-if="canViewConfig(config.type)"
            :key="index">
            <span v-if="index!==0">/</span>
            {{config.label.toLowerCase()}}
          </span>
        </small>
      </h5>
    </div>
    <Dialog
      width="360px"
      middle="true"
      :center="false"
      :title="$t('INDENT_DELETE_SOURCE')"
      :append-to-body="true"
      :visible.sync="showSourceDeleteDialog"
      class="default-dialog"
    >
      <span class="dialog-content" v-if="data && data[currentActiveIndex]">
        {{$t('INDENT_DELETE_SOURCE_CONFIRM_MSG')}} -
        <strong class="text-bold">{{data[currentActiveIndex].name}}</strong>?
      </span>
      <span slot="footer" class="dialog-footer">
        <Button type="secondary" size="mini" @click="showSourceDeleteDialog = false">{{$t('INDENT_CANCEL')}}</Button>
        <Button type="primary" size="mini" @click="deleteSource()">{{$t('INDENT_DELETE')}}</Button>
      </span>
    </Dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { get } from 'lodash';

export default {
  data() {
    return {
      showSourceDeleteDialog: false,
      expandView: false,
    };
  },
  props: {
    data: {
      default: () => ([]),
      type: Array,
    },
    currentActiveIndex: {
      default: 0,
      type: Number,
    },
    currentActivePickupIndex: {
      default: 0,
      type: Number,
    },
    selectionInProgress: {
      default: false,
      type: Boolean,
    },
    hideAllActions: {
      default: false,
      type: Boolean,
    },
    sourceConfig: {
      default: () => ([]),
      type: Array,
    },
    maxSelectionAllowed: {
      type: Number,
    },
    transporterPaneOpen: {
      type: Boolean,
      default: false,
    },
    showDelete: {
      type: Boolean,
      default: true,
    },
    canAddPickup: {
      type: Boolean,
      default: true,
    },
    order: {
      default: () => ({}),
      type: Object,
    },
  },
  computed: {
    ...mapGetters({
      configurations: 'app/configurations',
    }),
    noDataFound() {
      return !this.data || !this.data.length;
    },
    isIconVisible() {
      const subdomain = window.location.hostname.split('.')[0];
      return ['marico', 'development23'].includes(subdomain);
    },
  },
  methods: {
    canViewConfig(type) {
      const configByType = this.sourceConfig.find(val => val.type === parseInt(type, 10));
      return configByType.permission ? get(this.configurations, configByType.permission) : true;
    },
    deletePickup(pickupIndex, sourceIndex) {
      this.$emit('deletePickup', pickupIndex, sourceIndex);
    },
    deleteSource() {
      if (this.checkOrderStatusForChanges()) return null;
      this.$emit('deleteSource', this.currentActiveIndex);
      this.showSourceDeleteDialog = false;
      return true;
    },
    changeCurrentActiveSource(sourceIndex) {
      this.$emit('changeCurrentActiveSource', sourceIndex);
    },
    showPickupsByType(type) {
      const configByType = this.sourceConfig.find(val => val.type === parseInt(type, 10));
      return configByType && configByType.hasGates;
    },
    getSourceIconByType(type) {
      const configByType = this.sourceConfig.find(val => val.type === parseInt(type, 10));
      return (configByType && configByType.iconName) || 'icon-source';
    },
    checkOrderStatusForChanges() {
      const statusAfterTruckIn = [5, 17, 18]; // Arrived, Loading Start, Loading End
      if (!this.$hasPermission('enable_delete_pick_drop_after_arrived')) {
        if (this.order && this.order.status && statusAfterTruckIn.includes(this.order.status)) {
          this.$errorMessage({ message: 'Source Deletion not Allowed after Indent Arrived' });
          return true;
        }
        return false;
      }
      return false;
    },
  },
};
</script>
